import * as Sentry from "@sentry/react";
import { supabase } from '../lib/db.ts';

// Log levels
export enum LogLevel {
  DEBUG = 'DEBUG',
  INFO = 'INFO',
  WARN = 'WARN',
  ERROR = 'ERROR',
  SECURITY = 'SECURITY',
  TRAFFIC = 'TRAFFIC',
}

// Analytics event types
export enum AnalyticsEvent {
  PAGE_VIEW = 'PAGE_VIEW',
  USER_ACTION = 'USER_ACTION',
  ERROR = 'ERROR',
  PERFORMANCE = 'PERFORMANCE',
  LOGIN_FAILED = 'LOGIN_FAILED',
  LOGIN_SUCCESS = 'LOGIN_SUCCESS',
  TRAFFIC_SPIKE = 'TRAFFIC_SPIKE',
  SECURITY_ALERT = 'SECURITY_ALERT',
}

export interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  data?: unknown;
}

interface LoginAttempt {
  email: string;
  timestamp: string;
  success: boolean;
  ip?: string;
  userAgent?: string;
  reason?: string;
}

interface TrafficMetrics {
  timestamp: string;
  requestCount: number;
  route: string;
  userAgent: string;
  ip: string;
}

interface SecurityEvent {
  id: string;
  event_type: string;
  event_data: Record<string, unknown>;
  severity: string;
  event_timestamp: string;
  created_at: string;
}

interface FailedLoginRecord {
  id: string;
  email: string;
  login_timestamp: string;
  ip?: string;
  user_agent?: string;
  reason?: string;
  error_code?: string;
  error_type?: string;
  error_details?: string;
  extra_data?: Record<string, unknown>;
  created_at: string;
}

interface FailedLoginStatsRow {
  email: string;
  count: number;
}

interface TrafficStatsRow {
  route: string;
  count: number;
}

class Logger {
  private static instance: Logger;
  private logs: LogEntry[] = [];
  private loginAttempts: LoginAttempt[] = [];
  private trafficMetrics: TrafficMetrics[] = [];
  private readonly MAX_LOGS = 1000;
  private readonly MAX_LOGIN_ATTEMPTS = 500;
  private readonly MAX_TRAFFIC_METRICS = 1000;
  private readonly isDevelopment = import.meta.env.DEV;
  private requestCounts: Map<string, number> = new Map();
  private readonly TRAFFIC_SPIKE_THRESHOLD = 100; // requests per minute

  private constructor() {
    // Clean up old metrics every 10 minutes
    setInterval(() => {
      this.cleanupOldMetrics();
    }, 10 * 60 * 1000);
  }

  static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  private log(level: LogLevel, message: string, data?: unknown) {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      data,
    };

    this.logs.push(entry);
    
    // Keep logs array at a reasonable size
    if (this.logs.length > this.MAX_LOGS) {
      this.logs = this.logs.slice(-this.MAX_LOGS);
    }

    // Always log security and traffic events to console in production
    const shouldConsoleLog = this.isDevelopment || 
                           level === LogLevel.SECURITY || 
                           level === LogLevel.TRAFFIC;

    if (shouldConsoleLog) {
      const consoleMethod = level === LogLevel.ERROR ? 'error' :
                          level === LogLevel.WARN ? 'warn' :
                          level === LogLevel.INFO ? 'info' :
                          'log';
      
      console[consoleMethod](`[${level}] ${message}`, data || '');
    }

    // Send to external monitoring services
    if (level === LogLevel.SECURITY || level === LogLevel.ERROR) {
      this.sendToSentry(level, message, data);
    }
  }

  private sendToSentry(level: LogLevel, message: string, data?: unknown) {
    // Temporarily disabled to avoid 429 errors during debugging
    console.log(`[SENTRY DISABLED] ${level}: ${message}`, data);
    return;
    
    const sentryLevel = level === LogLevel.ERROR ? 'error' : 
                       level === LogLevel.SECURITY ? 'warning' : 'info';
    
    Sentry.withScope(scope => {
      scope.setTag('log_level', level);
      scope.setExtra('additional_data', data);
      
      if (data instanceof Error) {
        Sentry.captureException(data, { extra: { logMessage: message } });
      } else {
        Sentry.captureMessage(message, { 
          level: sentryLevel as 'error' | 'warning' | 'info', 
          extra: { errorDetails: data } 
        });
      }
    });
  }

  debug(message: string, data?: unknown) {
    if (this.isDevelopment) {
      this.log(LogLevel.DEBUG, message, data);
    }
  }

  info(message: string, data?: unknown) {
    this.log(LogLevel.INFO, message, data);
  }

  warn(message: string, data?: unknown) {
    this.log(LogLevel.WARN, message, data);
    this.sendToSentry(LogLevel.WARN, message, data);
  }

  error(message: string, error?: Error | unknown) {
    this.log(LogLevel.ERROR, message, error);
    this.sendToSentry(LogLevel.ERROR, message, error);

    // Track error analytics
    this.trackEvent(AnalyticsEvent.ERROR, {
      message,
      error: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
    });
  }

  // Security monitoring methods
  async logFailedLogin(email: string, reason: string = 'Invalid credentials', additionalData?: Record<string, unknown>) {
    const attempt: LoginAttempt = {
      email,
      timestamp: new Date().toISOString(),
      success: false,
      ip: this.getClientIP(),
      userAgent: navigator.userAgent,
      reason,
    };

    // Store in database using RPC function
    try {
      const { data, error } = await supabase.rpc('log_failed_login', {
        p_email: email,
        p_reason: reason,
        p_ip: attempt.ip,
        p_user_agent: attempt.userAgent,
        p_error_code: additionalData?.errorCode?.toString() || 'UNKNOWN',
        p_error_type: additionalData?.errorType?.toString() || 'AUTH_ERROR',
        p_error_details: additionalData?.errorDetails?.toString() || reason,
        p_extra_data: additionalData || {}
      });

      if (error) {
        console.error('Failed to log failed login to database:', error);
      } else if (!data) {
        console.error('Failed to log failed login: function returned false');
      }
    } catch (dbError) {
      console.error('Database error logging failed login:', dbError);
    }

    // Keep in-memory copy as fallback
    this.loginAttempts.push(attempt);
    
    // Keep attempts array at a reasonable size
    if (this.loginAttempts.length > this.MAX_LOGIN_ATTEMPTS) {
      this.loginAttempts = this.loginAttempts.slice(-this.MAX_LOGIN_ATTEMPTS);
    }

    // Log the security event
    this.log(LogLevel.SECURITY, `Failed login attempt for ${email}`, {
      ...attempt,
      ...additionalData,
      errorCode: additionalData?.errorCode || 'UNKNOWN',
      errorType: additionalData?.errorType || 'AUTH_ERROR',
      errorDetails: additionalData?.errorDetails || reason,
    });

    // Track analytics with enhanced error information
    this.trackEvent(AnalyticsEvent.LOGIN_FAILED, {
      email,
      reason,
      ip: attempt.ip,
      userAgent: attempt.userAgent,
      errorCode: additionalData?.errorCode || 'UNKNOWN',
      errorType: additionalData?.errorType || 'AUTH_ERROR',
      errorDetails: additionalData?.errorDetails || reason,
      timestamp: attempt.timestamp,
      ...additionalData,
    });

    // Check for suspicious activity using RPC function
    this.checkSuspiciousLoginActivity(email);
  }

  async logSuccessfulLogin(email: string, userId?: string, additionalData?: Record<string, unknown>) {
    const attempt: LoginAttempt = {
      email,
      timestamp: new Date().toISOString(),
      success: true,
      ip: this.getClientIP(),
      userAgent: navigator.userAgent,
    };

    // Store in database using RPC function
    try {
      const { data, error } = await supabase.rpc('log_successful_login', {
        p_email: email,
        p_user_id: userId,
        p_ip: attempt.ip,
        p_user_agent: attempt.userAgent,
        p_extra_data: additionalData || {}
      });

      if (error) {
        console.error('Failed to log successful login to database:', error);
      } else if (!data) {
        console.error('Failed to log successful login: function returned false');
      }
    } catch (dbError) {
      console.error('Database error logging successful login:', dbError);
    }

    // Log the event
    this.log(LogLevel.INFO, `Successful login for ${email}`, {
      ...attempt,
      userId,
      ...additionalData,
    });

    // Track analytics
    this.trackEvent(AnalyticsEvent.LOGIN_SUCCESS, {
      email,
      userId,
      ip: attempt.ip,
      userAgent: attempt.userAgent,
      timestamp: attempt.timestamp,
      ...additionalData,
    });
  }

  async trackRequest(route: string, additionalData?: Record<string, unknown>) {
    const timestamp = new Date().toISOString();
    const ip = this.getClientIP();
    const userAgent = navigator.userAgent;

    // Store in database using RPC function
    try {
      const { data, error } = await supabase.rpc('log_traffic_metric', {
        p_route: route,
        p_ip: ip,
        p_user_agent: userAgent,
        p_request_count: 1,
        p_extra_data: additionalData || {}
      });

      if (error) {
        console.error('Failed to log traffic to database:', error);
      } else if (!data) {
        console.error('Failed to log traffic: function returned false');
      }
    } catch (dbError) {
      console.error('Database error logging traffic:', dbError);
    }

    // Keep in-memory tracking for spike detection
    const metric: TrafficMetrics = {
      timestamp,
      requestCount: 1,
      route,
      userAgent,
      ip,
    };

    this.trafficMetrics.push(metric);
    
    // Keep metrics array at a reasonable size
    if (this.trafficMetrics.length > this.MAX_TRAFFIC_METRICS) {
      this.trafficMetrics = this.trafficMetrics.slice(-this.MAX_TRAFFIC_METRICS);
    }

    // Update request count for spike detection
    const routeKey = `${route}:${Math.floor(Date.now() / (60 * 1000))}`;
    const currentCount = this.requestCounts.get(routeKey) || 0;
    this.requestCounts.set(routeKey, currentCount + 1);

    // Log traffic event
    this.log(LogLevel.TRAFFIC, `Request tracked: ${route}`, {
      count: currentCount + 1,
      ip,
      userAgent,
      ...additionalData,
    });

    // Check for traffic spikes
    if (currentCount + 1 >= this.TRAFFIC_SPIKE_THRESHOLD) {
      this.logTrafficSpike(route, currentCount + 1, additionalData);
    }

    // Track analytics
    this.trackEvent(AnalyticsEvent.PAGE_VIEW, {
      route,
      ip,
      userAgent,
      timestamp,
      ...additionalData,
    });
  }

  private async logTrafficSpike(route: string, requestCount: number, additionalData?: Record<string, unknown>) {
    const eventData = {
      route,
      requestCount,
      threshold: this.TRAFFIC_SPIKE_THRESHOLD,
      timestamp: new Date().toISOString(),
      ...additionalData,
    };

    // Store security event in database using RPC function
    try {
      const { data, error } = await supabase.rpc('log_security_event', {
        p_event_type: 'TRAFFIC_SPIKE',
        p_event_data: eventData,
        p_severity: 'high'
      });

      if (error) {
        console.error('Failed to log traffic spike to database:', error);
      } else if (!data) {
        console.error('Failed to log traffic spike: function returned false');
      }
    } catch (dbError) {
      console.error('Database error logging traffic spike:', dbError);
    }

    // Log security event
    this.log(LogLevel.SECURITY, `Traffic spike detected on ${route}`, eventData);

    // Track analytics
    this.trackEvent(AnalyticsEvent.TRAFFIC_SPIKE, eventData);
  }

  private async checkSuspiciousLoginActivity(email: string) {
    try {
      const { data, error } = await supabase.rpc('check_suspicious_login_activity', {
        p_email: email,
        p_time_window_minutes: 5
      });

      if (error) {
        console.error('Error checking suspicious activity:', error);
        return;
      }

      if (data) {
        this.log(LogLevel.SECURITY, `Suspicious login activity detected for ${email}`, {
          email,
          timeWindow: '5 minutes',
          timestamp: new Date().toISOString()
        });
        this.trackEvent(AnalyticsEvent.SECURITY_ALERT, {
          email,
          type: 'suspicious_login_activity',
          timeWindow: '5 minutes'
        });
      }
    } catch (dbError) {
      console.error('Database error checking suspicious activity:', dbError);
    }
  }

  private cleanupOldMetrics() {
    const oneHourAgo = Date.now() - 60 * 60 * 1000;
    
    // Clean up in-memory traffic metrics
    this.trafficMetrics = this.trafficMetrics.filter(
      metric => new Date(metric.timestamp).getTime() > oneHourAgo
    );

    // Clean up request counts
    const currentMinute = Math.floor(Date.now() / (60 * 1000));
    for (const [key] of this.requestCounts) {
      const keyMinute = parseInt(key.split(':').pop() || '0');
      if (currentMinute - keyMinute > 60) {
        this.requestCounts.delete(key);
      }
    }

    // Clean up old login attempts (keep 24 hours in memory)
    const twentyFourHoursAgo = Date.now() - 24 * 60 * 60 * 1000;
    this.loginAttempts = this.loginAttempts.filter(
      attempt => new Date(attempt.timestamp).getTime() > twentyFourHoursAgo
    );
  }

  private getClientIP(): string {
    // In a real app, you might get this from headers or a service
    return 'unknown';
  }

  trackEvent(event: AnalyticsEvent, properties: Record<string, unknown> = {}) {
    // Track analytics event (could send to external service)
    if (this.isDevelopment) {
      console.log(`[ANALYTICS] ${event}:`, properties);
    }
  }

  private getUserId(): string | undefined {
    // Get current user ID if available
    return undefined;
  }

  // Database query methods for the dashboard using RPC functions
  async getFailedLoginStats(timeWindowMinutes: number = 60): Promise<{ email: string; count: number }[]> {
    try {
      const { data, error } = await supabase.rpc('get_failed_login_stats', {
        p_time_window_minutes: timeWindowMinutes
      });

      if (error) {
        console.error('Error fetching failed login stats:', error);
        return [];
      }

      return data?.map((row: FailedLoginStatsRow) => ({
        email: row.email,
        count: Number(row.count)
      })) || [];

    } catch (error) {
      console.error('Database error fetching failed login stats:', error);
      return [];
    }
  }

  async getTrafficStats(timeWindowMinutes: number = 60): Promise<{ route: string; count: number }[]> {
    try {
      const { data, error } = await supabase.rpc('get_traffic_stats', {
        p_time_window_minutes: timeWindowMinutes
      });

      if (error) {
        console.error('Error fetching traffic stats:', error);
        return [];
      }

      return data?.map((row: TrafficStatsRow) => ({
        route: row.route,
        count: Number(row.count)
      })) || [];

    } catch (error) {
      console.error('Database error fetching traffic stats:', error);
      return [];
    }
  }

  async getSecurityEvents(timeWindowMinutes: number = 60): Promise<SecurityEvent[]> {
    try {
      const { data, error } = await supabase.rpc('get_security_events', {
        p_time_window_minutes: timeWindowMinutes
      });

      if (error) {
        console.error('Error fetching security events:', error);
        return [];
      }

      return data || [];

    } catch (error) {
      console.error('Database error fetching security events:', error);
      return [];
    }
  }

  async getRecentFailedLogins(timeWindowMinutes: number = 60): Promise<FailedLoginRecord[]> {
    try {
      const { data, error } = await supabase.rpc('get_recent_failed_logins', {
        p_time_window_minutes: timeWindowMinutes
      });

      if (error) {
        console.error('Error fetching recent failed logins:', error);
        return [];
      }

      return data || [];

    } catch (error) {
      console.error('Database error fetching recent failed logins:', error);
      return [];
    }
  }

  // Legacy methods for backward compatibility (now return empty arrays)
  getLogs(): LogEntry[] {
    return this.logs;
  }

  getLoginAttempts(): LoginAttempt[] {
    return this.loginAttempts;
  }

  getTrafficMetrics(): TrafficMetrics[] {
    return this.trafficMetrics;
  }

  clearLogs() {
    this.logs = [];
  }

  clearLoginAttempts() {
    this.loginAttempts = [];
  }

  clearTrafficMetrics() {
    this.trafficMetrics = [];
  }

  // Test method to add sample data
  async addTestFailedLogins() {
    const testEmails = ['test1@example.com', 'test2@example.com', 'admin@test.com'];
    const testReasons = ['Invalid credentials', 'Account locked', 'Too many attempts'];
    
    for (let i = 0; i < 5; i++) {
      const email = testEmails[Math.floor(Math.random() * testEmails.length)];
      const reason = testReasons[Math.floor(Math.random() * testReasons.length)];
      await this.logFailedLogin(email, reason, {
        errorCode: '400',
        errorType: 'AUTH_ERROR',
        errorDetails: `Test failed login ${i + 1}`,
        timestamp: new Date(Date.now() - Math.random() * 3600000).toISOString()
      });
    }
  }
}

export const logger = Logger.getInstance();

// Expose logger globally for debugging in development
if (import.meta.env.DEV) {
  (window as unknown as { logger: Logger }).logger = logger;
} 